/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { validate } from "validate.js";
import {
  resetStateIncidents,
  updateIncident,
} from "../../app/slices/incidentsSlice";
import AppMessage from "../../components/AppMessage/AppMessage";
import IncidentForm from "./IncidentForm";
import moment from "moment";
import { oracleDateFormat } from "../../app/utils";

const UpdateIncidentDialog = ({
  open,
  onClose,
  title,
  selectedIncident,
  method = "VIEW",
}) => {
  const {
    data: dataPut,
    status: statusPut,
    error: errorPut,
  } = useSelector(({ incidents }) => incidents.put);

  validate.validators.datetime.parse = function (value) {
    return new Date(value); // Converts input to Date object
  };

  validate.validators.datetime.format = function (value, options) {
    const date = new Date(value);
    return options.dateOnly
      ? date.toISOString().split("T")[0] // Format as YYYY-MM-DD
      : date.toISOString();
  };

  const schema = {
    incidentCategory: {
      presence: { allowEmpty: false },
    },
    incidentDate: {
      presence: { allowEmpty: false },
      date: {
        dateOnly: true,
        latest: new Date(), // Ensures date is not in the future
        message: "^Incident date cannot be in the future",
      },
    },
    incidentDescription: {
      presence: { allowEmpty: false },
    },
    incidentRating: {
      presence: { allowEmpty: false },
    },
    tlpStatus: {
      presence: { allowEmpty: false },
    },
    incidentStatus: {
      presence: { allowEmpty: false },
    },
  };

  const schemaClosed = {
    causeOfIncident: {
      presence: { allowEmpty: false },
    },
    incidentLocation: {
      presence: { allowEmpty: false },
    },
    systemsImpacted: {
      presence: { allowEmpty: false },
    },
    sourceOfDetection: {
      presence: { allowEmpty: false },
    },
    measuresTaken: {
      presence: { allowEmpty: false },
    },
    lessonsLearned: {
      presence: { allowEmpty: false },
    },
  };

  const [formValues, setFormValues] = useState(selectedIncident || {});
  const [errors, setErrors] = useState([]);
  const [response, setResponse] = useState(null);
  const dispatch = useDispatch();

  //use effect
  useEffect(() => {
    dispatch(resetStateIncidents(["put"]));
    return () => {
      dispatch(resetStateIncidents(["put"]));
    };
  }, []);

  useEffect(() => {
    if (errorPut) {
      setResponse({
        type: "error",
        message: errorPut?.message,
      });
    }
  }, [errorPut]);

  useEffect(() => {
    if (dataPut) {
      setResponse({
        type: "success",
        message: dataPut,
      });
    }
  }, [dataPut]);

  //methods
  const handleSubmit = () => {
    let _errors = validate(
      formValues,
      formValues?.incidentStatus === "Closed"
        ? { ...schema, ...schemaClosed }
        : schema
    );

    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      setErrors([]);
    }

    dispatch(resetStateIncidents(["put"]));

    dispatch(
      updateIncident({
        id: selectedIncident?.incidentId,
        payload: {
          category: formValues?.incidentCategory,
          incident_date: moment(formValues?.incidentDate)?.format(
            oracleDateFormat
          ),
          rating: formValues?.incidentRating,
          status: formValues?.incidentStatus,
          tlp_status: formValues?.tlpStatus,
          description: formValues?.incidentDescription,
          location: formValues?.incidentLocation,
          cause: formValues?.causeOfIncident,
          detection_source: JSON.stringify(formValues?.sourceOfDetection || []),
          financial_impact: formValues?.financialImpact,
          non_financial_impact: JSON.stringify(
            formValues?.nonFinancialImpact || []
          ),
          impacted_softwares: formValues?.systemsImpacted,
          measures_taken: formValues?.measuresTaken,
          lessons_learned: formValues?.lessonsLearned,
          involved_persons: formValues?.namesOfPersonsInvolved,
        },
      })
    );
  };

  const handleChange = (e) => {
    const inputName = e?.target?.name;
    const inputValue = e?.target?.value;

    setFormValues({
      ...formValues,
      [inputName]: inputValue,
    });
  };

  //builders
  return (
    <Dialog
      fullWidth={true}
      maxWidth={"md"}
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">
        <span id="modal-header">
          <b>{title?.toUpperCase()}</b>
        </span>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <div className="p-10">
            <IncidentForm
              formValues={formValues}
              errors={errors}
              handleChange={handleChange}
              readOnly={method === "VIEW"}
            />
          </div>
          {response && (
            <AppMessage
              isOpen={response !== null}
              message={response?.message}
              messageType={response?.type}
              onClose={() => {
                setResponse(null);
              }}
            />
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {dataPut === null && method === "UPDATE" && (
          <Button
            id="btn-confirm"
            variant="contained"
            size="small"
            disabled={statusPut === "loading"}
            onClick={handleSubmit}
            color="primary"
          >
            {statusPut === "loading" && (
              <CircularProgress
                size={20}
                color="white"
                style={{ marginRight: "5px" }}
              />
            )}
            Update
          </Button>
        )}

        <Button
          id="btn-close"
          variant="contained"
          size="small"
          onClick={() => {
            onClose(dataPut ? true : false);
          }}
          color="error"
        >
          <CloseIcon fontSize="small" className="app-button-icon" /> Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateIncidentDialog;
